import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Navbar2 } from '../support/Navbar2';
import { FirebaseDB } from '../../firebase/config';
import { collection } from 'firebase/firestore/lite';
import { query, orderBy, onSnapshot } from 'firebase/firestore';
import moment from 'moment';
import 'moment/locale/es';
import { ContactReminderModal } from '../support/ContactReminderModal';
import { BulkPromotionModal } from '../support/BulkPromotionModal';
import {
    getSemanaActual,
    navegarSemana,
    formatSemanaLabel,
    calcularMetricasRiesgo,
    getCorreoLogsForSemana,
} from '../../services/seguimientoService';

moment.locale('es');

const giros = [
    "Todos",
    "Comercial",
    "Servicios Gubernamentales",
    "Manufactura/industrial",
    "Servicios",
    "Turístico",
    "Restaurantero o alimenticio",
    "Construcción",
    "Educativo",
    "Logístico o transporte",
    "Salud",
    "Agropecuario",
    "Tecnológico",
    "Otros"
];

export const SeguimientoScreen = () => {
    const { perfil } = useSelector(state => state.auth);
    const esAdmin = perfil === 'Administrador';

    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterName, setFilterName] = useState('');
    const [filterDiferencia, setFilterDiferencia] = useState('negativa');
    const [filterGiro, setFilterGiro] = useState('Todos');
    const [reminderCliente, setReminderCliente] = useState(null);
    const [bulkPromoOpen, setBulkPromoOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        setCurrentPage(1);
    }, [filterName, filterDiferencia, filterGiro]);

    // ── Estado del panel de estadísticas (solo Admin) ─────────
    const [semanaISO, setSemanaISO] = useState(getSemanaActual());
    const [correoLogs, setCorreoLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    // Fetch correoLogs cada vez que cambia la semana seleccionada
    const fetchCorreoLogs = useCallback(async (semana) => {
        if (!esAdmin) return;
        setLoadingLogs(true);
        try {
            const logs = await getCorreoLogsForSemana(semana);
            setCorreoLogs(logs);
        } catch (err) {
            console.error('Error cargando correoLogs:', err);
            setCorreoLogs([]);
        } finally {
            setLoadingLogs(false);
        }
    }, [esAdmin]);

    useEffect(() => {
        fetchCorreoLogs(semanaISO);
    }, [semanaISO, fetchCorreoLogs]);


    useEffect(() => {
        const collectionRef = collection(FirebaseDB, '/clientes');
        const q = query(collectionRef, orderBy("nombre", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = [];
            snapshot.docs.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setClientes(list);
            setLoading(false);
        }, (error) => {
            console.error("Error cargando clientes en SeguimientoScreen:", error);
            setLoading(false);
        });

        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, []);


    const perfilesPermitidos = ["Administrador", "Seguimiento"];
    if (!perfilesPermitidos.includes(perfil)) {
        return <Redirect to="/" />;
    }



    // ── Correos masivos agrupados por giro ────────────────────
    const correosPorGiro = correoLogs.reduce((acc, log) => {
        if (!acc[log.giro]) acc[log.giro] = { envios: 0, destinatarios: 0 };
        acc[log.giro].envios += 1;
        acc[log.giro].destinatarios += (log.cantidadDestinatarios || 0);
        return acc;
    }, {});
    const totalEnvios = correoLogs.length;
    const totalDestinatarios = correoLogs.reduce((s, l) => s + (l.cantidadDestinatarios || 0), 0);
    const semanaActual = getSemanaActual();


    const calculateDaysAndBadge = (fechaUltimoPedido, frecuenciaPedidos) => {
        if (!fechaUltimoPedido) {
            return {
                text: 'S/N',
                style: { backgroundColor: '#f3f4f6', color: '#4b5563', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
            };
        }
        const lastDate = moment(fechaUltimoPedido, 'YYYY-MM-DD');
        if (!lastDate.isValid()) {
            return {
                text: 'Inválida',
                style: { backgroundColor: '#fee2e2', color: '#dc2626', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
            };
        }
        const today = moment().startOf('day');
        const diff = today.diff(lastDate.startOf('day'), 'days');

        if (diff < 0) {
            return {
                text: 'Futura',
                style: { backgroundColor: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
            };
        }

        const text = `${diff} ${diff === 1 ? 'día' : 'días'}`;
        const frec = parseInt(frecuenciaPedidos);

        if (!isNaN(frec)) {
            const diferencia = frec - diff;
            if (diferencia >= 4) {
                return {
                    text,
                    style: { backgroundColor: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
                };
            } else if (diferencia >= 0 && diferencia <= 3) {
                return {
                    text,
                    style: { backgroundColor: '#fef3c7', color: '#b45309', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
                };
            } else {
                return {
                    text,
                    style: { backgroundColor: '#fee2e2', color: '#b91c1c', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
                };
            }
        }

        // Fallback si no tiene frecuencia
        if (diff <= 7) {
            return {
                text,
                style: { backgroundColor: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
            };
        } else if (diff <= 30) {
            return {
                text,
                style: { backgroundColor: '#fef3c7', color: '#b45309', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
            };
        } else {
            return {
                text,
                style: { backgroundColor: '#fee2e2', color: '#b91c1c', padding: '4px 10px', borderRadius: '12px', fontWeight: '600', fontSize: '11px', display: 'inline-block' }
            };
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return 'S/N';
        const parsed = moment(fecha, 'YYYY-MM-DD');
        return parsed.isValid() ? parsed.format('DD/MM/YYYY') : 'S/N';
    };


    // Helper to compute days since last order
    const getDiffDays = (fechaUltimoPedido) => {
        if (!fechaUltimoPedido) return -Infinity;
        const lastDate = moment(fechaUltimoPedido, 'YYYY-MM-DD');
        if (!lastDate.isValid()) return -Infinity;
        const today = moment().startOf('day');
        return today.diff(lastDate.startOf('day'), 'days');
    };

    // ── Métricas de riesgo calculadas en frontend ─────────────
    const getClientesEnRiesgoTotal = () => {
        return clientes.filter(c => {
            const frec = parseInt(c.frecuenciaPedidos);
            const dias = getDiffDays(c.fechaUltimoPedido);
            if (isNaN(frec) || dias === -Infinity) return false;
            return (frec - dias) < 0;
        });
    };

    const getClientesContactadosTotal = () => {
        return clientes.filter(c => !!c.ultimoContacto?.fecha);
    };

    const clientesEnRiesgoTotal = getClientesEnRiesgoTotal();
    const clientesContactadosTotal = getClientesContactadosTotal();

    const metricasRiesgo = esAdmin ? {
        enRiesgo: clientesEnRiesgoTotal.length,
        contactados: calcularMetricasRiesgo(clientes, semanaISO).contactados,
        tasa: clientesEnRiesgoTotal.length > 0
            ? Math.round((clientesContactadosTotal.length / clientesEnRiesgoTotal.length) * 100)
            : 0
    } : null;


    // Compute difference (Frecuencia - Días Transcurridos)
    const getDiferencia = (cliente) => {
        const frec = parseInt(cliente.frecuenciaPedidos);
        const dias = getDiffDays(cliente.fechaUltimoPedido);
        if (isNaN(frec) || dias === -Infinity) return Infinity;
        return frec - dias;
    };

    const filteredClientes = clientes.filter(c => {
        const matchesSearch =
            c.nombre.toLowerCase().includes(filterName.toLowerCase()) ||
            (c.giroCliente && c.giroCliente.toLowerCase().includes(filterName.toLowerCase())) ||
            (c.nombreComercial && c.nombreComercial.toLowerCase().includes(filterName.toLowerCase()));

        if (!matchesSearch) return false;

        if (filterGiro !== 'Todos') {
            if (c.giroCliente !== filterGiro) {
                return false;
            }
        }

        const diferencia = getDiferencia(c);

        if (filterDiferencia === 'negativa') {
            return diferencia < 0;
        } else if (filterDiferencia === 'cerca') {
            return diferencia >= 0 && diferencia <= 3;
        } else if (filterDiferencia === 'lejana') {
            return diferencia >= 4;
        }

        return true;
    });

    // Compute Cumplimiento de Frecuencia text
    const calculateCumplimiento = (frecuenciaPedidos, diff) => {
        const frec = parseInt(frecuenciaPedidos);
        if (isNaN(frec) || diff === -Infinity) return '';
        const diferencia = frec - diff;
        if (diferencia === frec) {
            return 'Realizó pedido hoy';
        }
        if (diferencia === 0) {
            return 'Hoy se cumple la frecuencia';
        }
        if (diferencia > 0) {
            return `Faltan ${diferencia} días`;
        }
        return `Se pasó ${Math.abs(diferencia)} días`;
    };

    // Sort clients ascending by diferencia (lowest first, includes negatives)
    const sortedClientes = [...filteredClientes].sort((a, b) => {
        return getDiferencia(a) - getDiferencia(b);
    });

    const totalPages = Math.ceil((sortedClientes?.length || 0) / itemsPerPage) || 1;
    const activePage = currentPage > totalPages ? totalPages : currentPage;
    const startIndex = (activePage - 1) * itemsPerPage;
    const paginatedClientes = sortedClientes.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div>
            <Navbar2 />
            <h1 className='settings__title'>Seguimiento de Clientes</h1>

            {/* --- Panel de Estadisticas (solo Administrador) --- */}
            {esAdmin && (
                <div className="seg-stats-panel">

                    {/* Navegador de semana */}
                    <div className="seg-week-nav">
                        <button
                            className="seg-week-nav__btn"
                            onClick={() => setSemanaISO(s => navegarSemana(s, -1))}
                            title="Semana anterior"
                        >‹</button>

                        <span className="seg-week-nav__label">
                            📅 {formatSemanaLabel(semanaISO)}
                        </span>

                        <button
                            className="seg-week-nav__btn"
                            onClick={() => setSemanaISO(s => navegarSemana(s, +1))}
                            disabled={semanaISO === semanaActual}
                            title="Semana siguiente"
                        >›</button>

                        {semanaISO !== semanaActual && (
                            <button
                                className="seg-week-nav__today-btn"
                                onClick={() => setSemanaISO(semanaActual)}
                            >Semana actual</button>
                        )}
                    </div>

                    {/* Cards */}
                    <div className="seg-stats-grid">

                        {/* Card 1 — Clientes en riesgo vs contactados */}
                        <div className="seg-stat-card">
                            <div className="seg-stat-card__header">
                                <span className="seg-stat-card__header-icon">📋</span>
                                <span className="seg-stat-card__header-title">Seguimiento de clientes</span>
                            </div>

                            <div className="seg-stat-numbers">
                                <div className="seg-stat-number">
                                    <span className="seg-stat-number__value seg-stat-number__value--riesgo">
                                        {metricasRiesgo.enRiesgo}
                                    </span>
                                    <span className="seg-stat-number__label">Total en riesgo</span>
                                </div>
                                <div className="seg-stat-number">
                                    <span className="seg-stat-number__value seg-stat-number__value--contactados">
                                        {metricasRiesgo.contactados}
                                    </span>
                                    <span className="seg-stat-number__label">Contactados<br />esta semana</span>
                                </div>

                            </div>

                            <div className="seg-coverage-bar">
                                <div className="seg-coverage-bar__label">
                                    <span>Cobertura Total Clientes fuera de tiempo</span>
                                    <strong>{metricasRiesgo.tasa}%</strong>
                                </div>
                                <div className="seg-coverage-bar__track">
                                    <div
                                        className="seg-coverage-bar__fill"
                                        style={{ width: `${metricasRiesgo.tasa}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Card 2 — Correos masivos enviados */}
                        <div className="seg-stat-card">
                            <div className="seg-stat-card__header">
                                <span className="seg-stat-card__header-icon">📧</span>
                                <span className="seg-stat-card__header-title">Correos masivos enviados</span>
                            </div>

                            <div className="seg-stat-numbers">
                                <div className="seg-stat-number">
                                    <span className="seg-stat-number__value seg-stat-number__value--enviados">
                                        {totalEnvios}
                                    </span>
                                    <span className="seg-stat-number__label">Envíos<br />esta semana</span>
                                </div>
                                <div className="seg-stat-number">
                                    <span className="seg-stat-number__value">
                                        {totalDestinatarios}
                                    </span>
                                    <span className="seg-stat-number__label">Destinatarios<br />en total</span>
                                </div>
                            </div>

                            {loadingLogs ? (
                                <div className="seg-loading">⏳ Cargando registros...</div>
                            ) : (
                                <div className="seg-giro-list">
                                    {Object.keys(correosPorGiro).length === 0 ? (
                                        <div className="seg-giro-list__empty">
                                            Sin correos masivos enviados esta semana.
                                        </div>
                                    ) : (
                                        Object.entries(correosPorGiro).map(([giro, datos]) => (
                                            <div key={giro} className="seg-giro-list__item">
                                                <span>{giro}</span>
                                                <span className="seg-giro-list__badge">
                                                    {datos.envios} envío{datos.envios !== 1 ? 's' : ''}{' \u00b7 '}{datos.destinatarios} dest.
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                    </div>{/* /seg-stats-grid */}
                </div>
            )}{/* /esAdmin */}


            <div style={{ width: '80%', margin: '0 auto 40px auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <p style={{ margin: 0, fontSize: '16px', color: '#374151', fontWeight: '500' }}>
                            Listado completo de actividad de clientes
                        </p>
                        <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>
                            Clientes Mostrados: {filteredClientes.length} de {clientes.length}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <select
                            value={filterDiferencia}
                            onChange={(e) => setFilterDiferencia(e.target.value)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                backgroundColor: '#ffffff',
                                color: '#374151',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="negativa" selected>Pasados de tiempo </option>
                            <option value="cerca">Cerca del límite (0 a 3 días)</option>
                            <option value="lejana">Lejanos al límite (4 o más días)</option>
                            <option value="todos">Todos los plazos</option>
                        </select>
                        <select
                            value={filterGiro}
                            onChange={(e) => setFilterGiro(e.target.value)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                backgroundColor: '#ffffff',
                                color: '#374151',
                                cursor: 'pointer'
                            }}
                        >
                            {giros.map(g => (
                                <option key={g} value={g}>{g === 'Todos' ? 'Todos los giros' : g}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            placeholder="Buscar por cliente o giro..."
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                width: '280px',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                            }}
                        />
                    </div>
                </div>

                <div className='dashboard__pedidos' style={{ overflowX: 'auto', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <p style={{ color: '#4b5563', fontSize: '15px' }}>Cargando información de clientes...</p>
                        </div>
                    ) : filteredClientes.length > 0 ? (
                        <table className="table table-striped table-hover mt-0" style={{ width: '100%', margin: '0', backgroundColor: 'white' }}>
                            <thead style={{ backgroundColor: '#f3f4f6' }}>
                                <tr>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Cliente</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Nombre Comercial</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Giro</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Último Pedido</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Frecuencia</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Días Transcurridos</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Cumplimiento de Frecuencia</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedClientes.map(cliente => {
                                    const badgeInfo = calculateDaysAndBadge(cliente.fechaUltimoPedido, cliente.frecuenciaPedidos); const diffDays = getDiffDays(cliente.fechaUltimoPedido);
                                    const cumplimiento = calculateCumplimiento(cliente.frecuenciaPedidos, diffDays);
                                    const diferencia = getDiferencia(cliente);
                                    const showReminder = true;
                                    return (
                                        <tr key={cliente.id} style={{ transition: 'background-color 0.15s' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: '500', color: '#111827', verticalAlign: 'middle' }}>{cliente.nombre}</td>
                                            <td style={{ padding: '12px 16px', fontWeight: '500', color: '#111827', verticalAlign: 'middle' }}>{cliente.nombreComercial || 'N/A'}</td>
                                            <td style={{ padding: '12px 16px', color: '#4b5563', verticalAlign: 'middle' }}>{cliente.giroCliente || 'S/N'}</td>
                                            <td style={{ padding: '12px 16px', color: '#4b5563', verticalAlign: 'middle' }}>{formatFecha(cliente.fechaUltimoPedido)}</td>
                                            <td style={{ padding: '12px 16px', color: '#4b5563', verticalAlign: 'middle' }}>{cliente.frecuenciaPedidos || 'S/N'}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                <span style={badgeInfo.style}>{badgeInfo.text}</span>
                                            </td>
                                            <td style={{ padding: '12px 16px', color: '#4b5563', verticalAlign: 'middle' }}>{cumplimiento}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                                    {showReminder && (
                                                        <button
                                                            title="Enviar recordatorio"
                                                            onClick={() => setReminderCliente(cliente)}
                                                            style={{
                                                                background: 'linear-gradient(135deg,#f59e0b,#d97706)',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                color: '#fff',
                                                                width: '34px',
                                                                height: '34px',
                                                                cursor: 'pointer',
                                                                fontSize: '16px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                transition: 'transform .15s, box-shadow .15s',
                                                                boxShadow: '0 2px 8px rgba(245,158,11,.3)',
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(245,158,11,.45)'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(245,158,11,.3)'; }}
                                                        >
                                                            🔔
                                                        </button>
                                                    )}
                                                    {cliente.ultimoContacto && (
                                                        <div style={{
                                                            fontSize: '10px',
                                                            color: '#4b5563',
                                                            backgroundColor: '#f3f4f6',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            marginTop: '2px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {cliente.ultimoContacto.canal === 'email' ? '📧 Correo' : '💬 WhatsApp'}
                                                            <br />
                                                            {cliente.ultimoContacto.fecha?.seconds
                                                                ? moment(cliente.ultimoContacto.fecha.seconds * 1000).format('DD/MM/YYYY')
                                                                : cliente.ultimoContacto.fecha?.toDate
                                                                    ? moment(cliente.ultimoContacto.fecha.toDate()).format('DD/MM/YYYY')
                                                                    : moment(cliente.ultimoContacto.fecha).isValid()
                                                                        ? moment(cliente.ultimoContacto.fecha).format('DD/MM/YYYY')
                                                                        : 'Reciente'
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <p style={{ color: '#4b5563', fontSize: '15px' }}>No se encontraron clientes registrados.</p>
                        </div>
                    )}
                </div>

                {sortedClientes.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px', marginBottom: '20px' }}>
                        <button
                            disabled={activePage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            style={{
                                padding: '8px 16px',
                                background: activePage === 1 ? '#e5e7eb' : '#3b82f6',
                                color: activePage === 1 ? '#9ca3af' : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: activePage === 1 ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                transition: 'background-color 0.2s',
                                boxShadow: activePage === 1 ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.2)'
                            }}
                        >
                            Anterior
                        </button>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                            Página {activePage} de {totalPages}
                        </span>
                        <button
                            disabled={activePage === totalPages}
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            style={{
                                padding: '8px 16px',
                                background: activePage === totalPages ? '#e5e7eb' : '#3b82f6',
                                color: activePage === totalPages ? '#9ca3af' : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: activePage === totalPages ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                transition: 'background-color 0.2s',
                                boxShadow: activePage === totalPages ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.2)'
                            }}
                        >
                            Siguiente
                        </button>
                    </div>
                )}

                {filterGiro !== 'Todos' && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '25px' }}>
                        <button
                            onClick={() => setBulkPromoOpen(true)}
                            style={{
                                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 24px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(29, 78, 216, 0.3)',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.03)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(29, 78, 216, 0.45)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(29, 78, 216, 0.3)';
                            }}
                        >
                            {'\u2709\ufe0f'} Enviar correo a los {sortedClientes.length} clientes del giro {'"'}{filterGiro}{'"'}
                        </button>
                    </div>
                )}
            </div>

            {/* --- Reminder Modal --- */}
            {reminderCliente && (
                <ContactReminderModal
                    cliente={reminderCliente}
                    onClose={() => setReminderCliente(null)}
                />
            )}

            {/* --- Bulk Promotion Modal --- */}
            {bulkPromoOpen && (
                <BulkPromotionModal
                    clientes={sortedClientes}
                    giro={filterGiro}
                    onClose={() => setBulkPromoOpen(false)}
                />
            )}
        </div>
    );
};
